import { LOCALE_TAGS } from '../../src/i18n/index';
import { T, api, errorText, esc, getLocale } from '../shared/client';

/**
 * Guest book: everyone who has booked, with search, tags, notes, visit
 * counters and CSV export. Counters come from the server (computed from
 * reservations), so what you see here matches the host stand.
 */
interface Opts {
  restaurantId: string;
  canExport: boolean;
  toast: (msg: string, isErr?: boolean) => void;
  onClose: () => void;
}

const NOTE_KEYS = ['general', 'relationship', 'food_drink', 'seating'] as const;

export function openGuestsScreen(opts: Opts): void {
  const root = document.createElement('div');
  root.className = 'cfg-root gb-root';
  document.body.appendChild(root);
  document.body.classList.add('cfg-open');

  const base = `/v1/restaurants/${opts.restaurantId}`;
  let list: any = { total: 0, guests: [], tags: [] };
  let search = '';
  let tag = '';
  let sort = 'name';
  let detail: any = null;
  let tab: 'guests' | 'tags' = 'guests';
  let timer = 0 as any;

  const day = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { dateStyle: 'medium' }).format(new Date(iso)) : '—';
  const fail = (e: unknown) => opts.toast(errorText(e), true);

  async function load() {
    const q = new URLSearchParams({ sort, limit: '200' });
    if (search) q.set('search', search);
    if (tag) q.set('tag', tag);
    try {
      list = await api('GET', `${base}/guests?${q}`);
    } catch (e) {
      fail(e);
    }
    render();
  }

  function rowsHtml(): string {
    return list.guests
      .map(
        (g: any) => `<tr data-open="${g.id}" tabindex="0" role="button">
        <td><strong>${esc(g.name)}</strong>${g.company ? `<br><span class="muted small">${esc(g.company)}</span>` : ''}</td>
        <td class="small">${esc(g.phone ?? '')}<br>${esc(g.email ?? '')}</td>
        <td class="num">${g.visits}</td><td class="num">${g.upcoming}</td>
        <td class="num">${g.cancellations}</td><td class="num">${g.noShows}</td>
        <td class="small">${esc(day(g.lastVisit))}</td>
        <td>${[...g.tags, ...g.autoTags].map((t: string) => `<span class="chip">${esc(t)}</span>`).join(' ')}</td></tr>`,
      )
      .join('');
  }

  function guestsView(): string {
    const tagOpts = (list.tags as any[]).map((t) => `<option value="${esc(t.name)}" ${tag === t.name ? 'selected' : ''}>${esc(t.name)}</option>`).join('');
    return `<div class="cfg-row gb-bar">
        <input id="gb-search" type="search" value="${esc(search)}" placeholder="${esc(T('g.search'))}" aria-label="${esc(T('g.search'))}">
        <label>${esc(T('g.tag'))} <select id="gb-tag"><option value="">${esc(T('g.all_tags'))}</option>${tagOpts}</select></label>
        <label>${esc(T('g.sort'))} <select id="gb-sort">${['name', 'visits', 'last_visit', 'created']
          .map((s) => `<option value="${s}" ${sort === s ? 'selected' : ''}>${esc(T(`g.sort.${s}`))}</option>`)
          .join('')}</select></label>
        <span class="spacer"></span>
        <span class="muted small">${esc(T('g.count', { n: list.total }))}</span>
        <button data-do="new">${esc(T('g.new'))}</button>
        ${opts.canExport ? `<a class="btn" href="${base}/guests/export" download>${esc(T('g.export'))}</a>` : ''}
      </div>
      <table class="cfg-table gb-table"><thead><tr><th>${esc(T('g.col.name'))}</th><th>${esc(T('g.col.contact'))}</th>
        <th class="num">${esc(T('g.col.visits'))}</th><th class="num">${esc(T('g.col.upcoming'))}</th><th class="num">${esc(T('g.col.cancellations'))}</th>
        <th class="num">${esc(T('g.col.noshows'))}</th><th>${esc(T('g.col.last'))}</th><th>${esc(T('g.col.tags'))}</th></tr></thead>
        <tbody>${rowsHtml() || `<tr><td colspan="8" class="muted">${esc(T('g.none'))}</td></tr>`}</tbody></table>`;
  }

  function tagsView(): string {
    const rows = (list.tags as any[])
      .map(
        (t) => `<tr data-tag="${t.id}">
        <td><input data-f="name" value="${esc(t.name)}" maxlength="30"></td>
        <td><input data-f="color" type="color" value="${esc(t.color)}"></td>
        <td><input data-f="min" type="number" min="1" max="1000" value="${t.minVisits ?? ''}" placeholder="${esc(T('g.manual'))}"></td>
        <td><input data-f="priority" type="checkbox" ${t.priority ? 'checked' : ''}></td>
        <td class="num">${t.guests}</td>
        <td><button data-do="tag-save" data-id="${t.id}">${esc(T('common.save'))}</button> <button class="danger" data-do="tag-del" data-id="${t.id}">${esc(T('g.delete'))}</button></td></tr>`,
      )
      .join('');
    return `<p class="muted small">${esc(T('g.tags_hint'))}</p>
      <table class="cfg-table"><thead><tr><th>${esc(T('g.tag_name'))}</th><th>${esc(T('g.tag_color'))}</th><th>${esc(T('g.tag_auto'))}</th><th>${esc(T('x.tag.priority'))}</th><th class="num">${esc(T('g.col.tags'))}</th><th></th></tr></thead>
      <tbody>${rows}
        <tr id="tag-new"><td><input data-f="name" maxlength="30" placeholder="${esc(T('g.tag_new'))}"></td><td><input data-f="color" type="color" value="#8b95a5"></td>
        <td><input data-f="min" type="number" min="1" max="1000" placeholder="${esc(T('g.manual'))}"></td><td><input data-f="priority" type="checkbox"></td><td></td>
        <td><button class="primary" data-do="tag-add">${esc(T('g.add'))}</button></td></tr></tbody></table>`;
  }

  function detailView(): string {
    const g = detail;
    const manualTags = (list.tags as any[]).filter((t) => t.minVisits == null);
    const have = new Set<string>(g.tags ?? []);
    const f = (id: string, label: string, v: string | null, type = 'text') =>
      `<label>${esc(label)} <input id="${id}" type="${type}" value="${esc(v ?? '')}" ${id === 'd-email' || id === 'd-phone' ? 'maxlength="200"' : 'maxlength="120"'}></label>`;
    return `<div class="gb-detail card">
      <div style="display:flex;justify-content:space-between;gap:8px"><h3>${esc(g.id ? g.name : T('g.new'))}</h3>
        <button class="ghost" data-do="detail-close" aria-label="${esc(T('common.close'))}">✕</button></div>
      ${g.id ? `<div class="counters"><span><strong>${g.upcoming}</strong>${esc(T('g.col.upcoming'))}</span><span><strong>${g.visits}</strong>${esc(T('g.col.visits'))}</span>
        <span><strong>${g.cancellations}</strong>${esc(T('g.col.cancellations'))}</span><span><strong>${g.noShows}</strong>${esc(T('g.col.noshows'))}</span></div>` : ''}
      <div class="cfg-row">${f('d-first', T('g.first'), g.firstName)}${f('d-last', T('g.last'), g.lastName)}</div>
      <div class="cfg-row">${f('d-phone', T('g.phone'), g.phone, 'tel')}${f('d-email', T('g.email'), g.email, 'email')}${f('d-company', T('g.company'), g.company)}</div>
      <div class="cfg-row"><label class="cfg-check"><input type="checkbox" id="d-mkt" ${g.marketingOptIn ? 'checked' : ''}> ${esc(T('g.marketing'))}</label>
        <label class="cfg-check"><input type="checkbox" id="d-sms" ${g.smsOptIn ? 'checked' : ''}> ${esc(T('g.sms'))}</label></div>
      <div class="tagpick">${manualTags
        .map((t) => `<label class="tagchip" style="--c:${esc(t.color)}"><input type="checkbox" data-tag="${esc(t.name)}" ${have.has(t.name) ? 'checked' : ''}> ${esc(t.name)}</label>`)
        .join('')}${(g.autoTags ?? []).map((t: string) => `<span class="chip">${esc(t)}</span>`).join(' ')}</div>
      ${NOTE_KEYS.map((k) => `<label>${esc(T(`g.note.${k}`))} <input data-note="${k}" maxlength="500" value="${esc(g.profileNotes?.[k] ?? '')}"></label>`).join('')}
      <div class="cfg-row"><button class="primary" data-do="detail-save">${esc(T('common.save'))}</button>
        ${g.id ? `<button data-do="detail-hide">${esc(g.hidden ? T('g.unhide') : T('g.hide'))}</button>` : ''}</div>
      ${g.id ? `<h4>${esc(T('g.history'))}</h4>${
        (g.reservations ?? []).length
          ? (g.reservations as any[]).map((r) => `<div class="small">${esc(day(r.startsAt))} · ${r.partySize} · ${esc(r.status)} · ${esc(r.source)}${r.specialRequest ? ' · ' + esc(r.specialRequest) : ''}</div>`).join('')
          : `<p class="muted small">${esc(T('g.no_history'))}</p>`
      }` : ''}
    </div>`;
  }

  function render() {
    const keepSearch = document.activeElement?.id === 'gb-search';
    root.innerHTML = `
      <div class="cfg-bar"><strong>${esc(T('g.title'))}</strong>
        <span class="seg" role="tablist"><button role="tab" data-do="tab" data-t="guests" aria-selected="${tab === 'guests'}">${esc(T('g.tab.guests'))}</button>
          <button role="tab" data-do="tab" data-t="tags" aria-selected="${tab === 'tags'}">${esc(T('g.tab.tags'))}</button></span>
        <span class="spacer"></span><button data-do="close">${esc(T('common.close'))}</button></div>
      <div class="cfg-body gb-body ${detail ? 'with-detail' : ''}"><main class="cfg-main">${tab === 'guests' ? guestsView() : tagsView()}</main>${detail ? detailView() : ''}</div>`;
    if (keepSearch) {
      const el = root.querySelector('#gb-search') as HTMLInputElement;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }

  async function open(id: string) {
    try {
      detail = await api('GET', `${base}/guests/${id}`);
      render();
    } catch (e) {
      fail(e);
    }
  }

  async function saveDetail() {
    const v = (id: string) => (root.querySelector(`#${id}`) as HTMLInputElement).value.trim();
    const tags = [...root.querySelectorAll<HTMLInputElement>('[data-tag]')].filter((c) => c.checked).map((c) => c.dataset.tag!);
    const profileNotes: Record<string, string> = {};
    root.querySelectorAll<HTMLInputElement>('[data-note]').forEach((i) => (profileNotes[i.dataset.note!] = i.value.trim()));
    const body = {
      firstName: v('d-first'), lastName: v('d-last'), phone: v('d-phone') || null, email: v('d-email') || null, company: v('d-company') || null,
      marketingOptIn: (root.querySelector('#d-mkt') as HTMLInputElement).checked,
      smsOptIn: (root.querySelector('#d-sms') as HTMLInputElement).checked,
      tags, profileNotes,
    };
    try {
      detail = detail.id ? await api('PATCH', `${base}/guests/${detail.id}`, body) : await api('POST', `${base}/guests`, body);
      opts.toast(T('g.saved'));
      await load();
    } catch (e) {
      fail(e);
    }
  }

  async function tagCall(method: string, path: string, row: HTMLElement | null) {
    const f = (n: string) => (row?.querySelector(`[data-f="${n}"]`) as HTMLInputElement | null)?.value.trim() ?? '';
    const body = method === 'DELETE' ? undefined : { name: f('name'), color: f('color') || '#8b95a5', minVisits: f('min') ? Number(f('min')) : null, priority: !!(row?.querySelector('[data-f="priority"]') as HTMLInputElement | null)?.checked };
    try {
      await api(method, path, body);
      await load();
    } catch (e) {
      fail(e);
    }
  }

  root.addEventListener('input', (ev) => {
    const el = ev.target as HTMLInputElement;
    if (el.id === 'gb-search') {
      search = el.value;
      clearTimeout(timer);
      timer = setTimeout(load, 250);
    }
  });
  root.addEventListener('change', (ev) => {
    const el = ev.target as HTMLSelectElement;
    if (el.id === 'gb-tag') {
      tag = el.value;
      void load();
    } else if (el.id === 'gb-sort') {
      sort = el.value;
      void load();
    }
  });
  root.addEventListener('click', async (ev) => {
    const t = ev.target as HTMLElement;
    const row = t.closest('[data-open]') as HTMLElement | null;
    if (row && !t.closest('button')) return void (await open(row.dataset.open!));
    const b = t.closest('[data-do]') as HTMLElement | null;
    if (!b) return;
    switch (b.dataset.do) {
      case 'close':
        root.remove();
        document.body.classList.remove('cfg-open');
        opts.onClose();
        return;
      case 'tab':
        tab = b.dataset.t as 'guests' | 'tags';
        return render();
      case 'new':
        detail = { id: null, firstName: '', lastName: '', tags: [], profileNotes: {}, reservations: [] };
        return render();
      case 'detail-close':
        detail = null;
        return render();
      case 'detail-save':
        return void (await saveDetail());
      case 'detail-hide':
        try {
          detail = await api('PATCH', `${base}/guests/${detail.id}`, { hidden: !detail.hidden });
          await load();
        } catch (e) {
          fail(e);
        }
        return;
      case 'tag-add':
        return void (await tagCall('POST', `${base}/guest-tags`, root.querySelector('#tag-new')));
      case 'tag-save':
        return void (await tagCall('PUT', `${base}/guest-tags/${b.dataset.id}`, b.closest('tr')));
      case 'tag-del':
        if (!window.confirm(T('g.delete_confirm'))) return;
        return void (await tagCall('DELETE', `${base}/guest-tags/${b.dataset.id}`, null));
    }
  });
  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') (root.querySelector('[data-do="close"]') as HTMLElement | null)?.click();
  });

  void load();
}
